/**
 * Inline confirmation panel shared by the transaction feed (Dashboard) and the
 * Reverse page: "Reverse R55.00 · Groceries · 25 Sept?" with Confirm/Cancel.
 *
 * Rendered inline (not as a modal) so the surrounding context stays visible.
 * Accessibility: a `role="group"` labelled by its heading, focus moves to the
 * Confirm button when the panel appears, Escape cancels, and the panel is
 * announced via `aria-live="polite"` on the wrapper. Callers own the state
 * (which item is being confirmed) and the actual API call — this component
 * never issues requests itself.
 */

import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

export interface ConfirmActionProps {
  /** Short heading, e.g. "Reverse this transaction?". */
  readonly title: string;
  /** Optional summary body (the amount/description/date lines). */
  readonly children?: ReactNode;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  /** Label shown on the confirm button while the action is in flight. */
  readonly pendingLabel?: string;
  /** Disables both buttons and swaps the confirm label while true. */
  readonly pending?: boolean;
  /** Visual tone of the confirm button (danger for destructive actions). */
  readonly tone?: 'primary' | 'danger';
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/** Accessible inline confirm panel: heading + summary + Confirm/Cancel row. */
export function ConfirmAction({
  title,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  pendingLabel = 'Working…',
  pending = false,
  tone = 'primary',
  onConfirm,
  onCancel,
}: ConfirmActionProps): JSX.Element {
  const headingId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Move focus to the primary action when the panel appears.
  useEffect(() => {
    confirmRef.current?.focus();
  }, []);

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape' && !pending) {
      event.stopPropagation();
      onCancel();
    }
  }

  const confirmClass =
    tone === 'danger' ? 'esb-btn esb-btn--danger' : 'esb-btn esb-btn--primary';

  return (
    <div aria-live="polite">
      <div
        role="group"
        aria-labelledby={headingId}
        className="esb-confirm"
        onKeyDown={onKeyDown}
      >
        <h4 id={headingId} className="esb-confirm__title">{title}</h4>
        {children !== undefined ? <div className="esb-confirm__body">{children}</div> : null}
        <div className="esb-confirm__actions">
          <button
            ref={confirmRef}
            type="button"
            className={confirmClass}
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? pendingLabel : confirmLabel}
          </button>
          <button
            type="button"
            className="esb-btn esb-btn--ghost"
            onClick={onCancel}
            disabled={pending}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
